import React from 'react';
import type { Notification, Page } from '../types.ts';
import { NotificationCategory } from '../types.ts';
import { 
    CurrencyDollarIcon, ShoppingCartIcon, ExclamationTriangleIcon, MegaphoneIcon, ArrowUpRightIcon
} from '@heroicons/react/24/outline';

const categoryIcons: Record<NotificationCategory, any> = {
    [NotificationCategory.Payment]: CurrencyDollarIcon,
    [NotificationCategory.Order]: ShoppingCartIcon,
    [NotificationCategory.Error]: ExclamationTriangleIcon,
    [NotificationCategory.System]: MegaphoneIcon,
};

// Helper to format time
const formatRelativeTime = (isoDate: string) => {
    const date = new Date(isoDate);
    const now = new Date();
    const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.round(seconds / 60);
    const hours = Math.round(minutes / 60);
    const days = Math.round(hours / 24);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
};


interface NotificationItemProps {
    notification: Notification;
    onNavigate: (page: Page, initialState?: any) => void;
    onClose: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onNavigate, onClose }) => {
    const Icon = categoryIcons[notification.category];
    const statusDotColor = notification.isRead ? 'bg-gray-300' : 'bg-blue-500';

    const handleClick = () => {
        // In a real app, clicking would also mark it as read via an API call
        if (notification.link) {
            onNavigate(notification.link.page, notification.link.initialState);
            onClose();
        }
    };
    
    return (
        <div 
            onClick={handleClick}
            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
            role="button"
            tabIndex={0}
        >
            <div className="flex-shrink-0 mt-1.5">
                <span className={`block w-2 h-2 rounded-full ${statusDotColor}`}></span>
            </div>
            <div className="flex-shrink-0">
                <div className="p-2 bg-gray-100 rounded-full">
                    <Icon className="h-5 w-5 text-text-subtle" />
                </div>
            </div>
            <div className="flex-grow">
                <p className={`text-sm ${!notification.isRead ? 'font-semibold text-text' : 'text-text-subtle'}`}>
                    {notification.title}
                </p>
                <p className="text-xs text-text-subtle mt-0.5">{formatRelativeTime(notification.timestamp)}</p>
            </div>
            {notification.link && (
                 <div className="flex-shrink-0 self-center">
                    <ArrowUpRightIcon className="h-4 w-4 text-text-subtle" />
                </div>
            )}
        </div>
    );
};

export default NotificationItem;
